/**
 * Video Player Page
 *
 * Player HTML5 de uma vídeo-aula. O arquivo não é público: o backend emite um
 * link assinado de curta duração (`/playback`) usado tanto para reproduzir
 * quanto para baixar. Como a rota suporta Range, o player avança a aula sem
 * rebaixar o arquivo inteiro.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useVideo, useVideoPlayback } from '@/hooks/use-learning';
import { resolveMediaUrl } from '@/lib/api';

export default function VideoPlayerPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: video, isLoading, isError } = useVideo(id);
  const { data: playback, isLoading: isLoadingPlayback } = useVideoPlayback(id);

  const isBusy = isLoading || isLoadingPlayback;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Button variant="ghost" className="gap-2 -ml-2" onClick={() => navigate('/learn/videos')}>
        <ArrowLeft className="h-4 w-4" />
        Voltar para as Vídeo-aulas
      </Button>

      {(() => {
        if (isBusy) return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (isError || !video) return (
            <Card>
              <CardContent className="py-12 text-center">
                <Video className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">Vídeo-aula não encontrada</h3>
                <p className="text-muted-foreground">O vídeo pode ter sido removido ou despublicado.</p>
              </CardContent>
            </Card>
        );
        return (
            <div className="space-y-4">
              <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
                {playback ? (
                  <video
                    key={playback.stream_url}
                    controls
                    preload="metadata"
                    className="h-full w-full"
                    poster={video.thumbnail_url ? resolveMediaUrl(video.thumbnail_url) : undefined}
                    src={resolveMediaUrl(playback.stream_url)}
                  >
                    Seu navegador não suporta a reprodução de vídeo.
                  </video>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-white/70">
                    Não foi possível liberar o vídeo. Recarregue a página e tente novamente.
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold">{video.title}</h1>
                  {video.description && (
                    <p className="text-muted-foreground mt-2 whitespace-pre-line">
                      {video.description}
                    </p>
                  )}
                </div>
                {playback && (
                  <Button asChild variant="outline" className="gap-2 shrink-0">
                    {/* Download direto: mesma rota assinada, com Content-Disposition. */}
                    <a href={resolveMediaUrl(playback.download_url)} download>
                      <Download className="h-4 w-4" />
                      Baixar vídeo
                    </a>
                  </Button>
                )}
              </div>
            </div>
        );
      })()}
    </div>
  );
}
